/* @flow */
import { isEmpty, sortBy, filter } from 'lodash';
import * as React from 'react';
import Modal from 'react-modal';

import ToastActions from 'actions/ToastActions';
import { promiseRelationship } from 'chat/utils/ChatPromises';
import AddButton from 'components/AddButton';
import Avatar from 'components/Avatar';
import AvatarWithTextItem from 'components/AvatarWithTextItem';
import AvatarWithTextItems from 'components/AvatarWithTextItems';
import Buttons from 'components/Buttons';
import ClipboardTrigger from 'components/ClipboardTrigger';
import Icon from 'components/Icon';
import Profile from 'components/Profile';
import Text from 'components/Text';
import Textarea from 'components/Textarea';
import Tooltip from 'components/Tooltip';
import TooltipTrigger from 'components/TooltipTrigger';
import RosterConstants from 'constants/RosterConstants';
import Group from 'entities/Group';
import Relationship from 'entities/Relationship';
import CopySubscriberDialog from 'groups/components/CopySubscriberDialog';
import GroupMemberFamily from 'groups/components/GroupMemberFamily';
import { format } from 'lib/phoneNumbers';
import PhoneCallButton from 'phoneCalls/components/PhoneCallButton';
import StartChatButton from 'user/components/StartChatButton';
import UserActionsMenuButton from 'user/components/UserActionsMenuButton';
import UsersAvatarWithText from 'users/components/UsersAvatarWithText';
import errorLogger from 'utils/errorLogger';
import { subtextForMember } from './utils';
import mapGraphQLMemberToUserActionsMenuButtonUser from 'user/lib/mapGraphQLMemberToUserActionsMenuButtonUser';

import type { DeviceType, OrganizationRole } from 'queries/__generated__/globalGQLTypes';
import type { GroupProfileType } from 'groups/components/GroupProfile';
import type { DenialReason } from 'phoneCalls/components/PhoneCallModal';

import './UsersProfile.scss';

export type UserProfileUser = {};

type GroupAvatar = {
  +id: number,
  +color: ?string,
  +svgUrl: ?string,
};

type GroupInfo = {
  +id: number,
  +uuid: string,
  +className: string,
  +groupAvatar: ?GroupAvatar,
  +name: string,
  +hasRosterSource: boolean,
};

type AdminVisibleDeviceDetails = {
  +id: number,
  +endpoint: string,
  +type: DeviceType,
};

export type UserProfile = {
  id: number,
  uuid: string,
  name: string,
  firstName: string,
  lastName: string,
  note: string,

  familySummary: {
    parents: $ReadOnlyArray<{
      +id: number,
      +uuid: string,
      +name: ?string,
      +color: ?string,
      +initials: ?string,
      +profilePictureUrl: ?string,
    }>,
    children: $ReadOnlyArray<{
      +id: number,
      +uuid: string,
      +name: ?string,
      +color: ?string,
      +initials: ?string,
      +profilePictureUrl: ?string,
    }>,
  },
  userInteractionPermissions: {
    +canAddChild: boolean,
    +canAddAsGroupOwner: boolean,
    +canAddParent: boolean,
    +canAssignRole: boolean,
    +canBlock: boolean,
    +canChangeRole: boolean,
    +canChat: boolean,
    +canCopyToAnotherGroup: boolean,
    +canRemoveFromGroup: boolean,
    +canReportToRemind: boolean,
    +canStartAChat: boolean,
    +canViewProfile: boolean,
    +canUnblock: boolean,
    +canPhone: boolean,
    +canPhoneDenialReason: ?string,
    +canSetNote: boolean,
    +canRequestMessageHistory: boolean,
    +canGetMessageHistory: boolean,
  },
  groupsOwned: $ReadOnlyArray<GroupInfo>,

  // Specific to org, but set to empty array for group contexts
  roles: OrganizationRole[],
  potentialRoles: $ReadOnlyArray<{
    +role: 'admin' | 'parent' | 'rce' | 'student' | 'teacher' | 'unknown',
    +display_role: string,
  }>,

  // Specific to org context
  adminVisibleDeviceDetails?: $ReadOnlyArray<AdminVisibleDeviceDetails>,
  groupsJoined?: $ReadOnlyArray<GroupInfo>,
  grade?: ?string,
  rosteredStatus?: boolean,

  // Specific to group context
  devices: *[],
  groupsInCommon?: $ReadOnlyArray<
    GroupInfo & {
      +isMember: boolean,
    }
  >,
};

export type Props = {
  member: UserProfile,
  onBackClicked?: () => void,
  onClose: () => void,
  onGroupClicked: (group: GroupProfileType) => void,
  onFamilyMemberClicked: (user: UserProfile, role: string) => void,
  onNoteChange: (note: string) => void,
  onUserChanged: () => void,
  trackEvent: (string, ?Object) => void,
  userActionsMenuButton: React.Element,
};

type State = {
  addGroupModalRelationship: ?Relationship,
  note: string,
  noteChanged: boolean,
  showAddGroupModal: boolean,
};

const avatarWithTextProps = {
  subtle: true,
};

export default class UsersProfile extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      addGroupModalRelationship: null,
      note: props.member.note,
      noteChanged: false,
      showAddGroupModal: false,
    };
  }

  closeModal = () => {
    this.setState({
      addGroupModalRelationship: null,
      showAddGroupModal: false,
    });
  };

  handleEmailCopy = () => {
    ToastActions.showSuccessToast('Email copied to clipboard');
    this.props.trackEvent('user_profile.device.copied');
  };

  adminDevicesList = (devices: AdminVisibleDeviceDetails[]) => {
    if (isEmpty(devices)) {
      return null;
    }

    const devicesSorted = sortBy(devices, item => (item.type === 'sms' ? 0 : 1));

    return (
      <AvatarWithTextItems>
        {devicesSorted.map(item => {
          const { endpoint, id, type } = item;

          const isEmail = type === 'email';

          const avatarWithTextItem = (
            <AvatarWithTextItem
              {...avatarWithTextProps}
              avatar={<Avatar glyphName={isEmail ? 'email-stroke' : 'phone'} />}
              key={id}
              subtext={isEmail ? 'Email' : 'Phone'}
              title={isEmail ? endpoint : format(endpoint)}
              data-test="device-item"
            />
          );

          if (isEmail) {
            return (
              <TooltipTrigger
                placement="top"
                tooltip={
                  <Tooltip>
                    Click to copy <Text wrap="force">{endpoint}</Text>
                  </Tooltip>
                }>
                <div>
                  <ClipboardTrigger text={endpoint} onCopy={this.handleEmailCopy}>
                    {avatarWithTextItem}
                  </ClipboardTrigger>
                </div>
              </TooltipTrigger>
            );
          }

          return avatarWithTextItem;
        })}
      </AvatarWithTextItems>
    );
  };

  groupsList = (groups: $ReadOnlyArray<GroupInfo>, groupType: 'owned' | 'joined') => {
    const { onGroupClicked, onUserChanged, member } = this.props;
    const {
      userInteractionPermissions: { canRemoveFromGroup },
    } = member;

    if (isEmpty(groups)) {
      return null;
    }

    return (
      <AvatarWithTextItems>
        {groups.map(item => {
          const groupEntity = ((Group.fromJSON(item): any): Group);
          return (
            <AvatarWithTextItem
              {...avatarWithTextProps}
              key={item.uuid}
              action={
                groupType === 'joined' ? (
                  <UserActionsMenuButton
                    buttonSize="xlarge"
                    level="icon"
                    glyphSize="inherit"
                    groupToAdmin={groupEntity}
                    isAdminView
                    showRemoveMember
                    disableRemoveMember={!canRemoveFromGroup}
                    onUserRemoved={onUserChanged}
                    user={mapGraphQLMemberToUserActionsMenuButtonUser(member)}
                    tooltip="More actions"
                    tooltipPlacement="top"
                  />
                ) : null
              }
              avatarSize="small"
              group={groupEntity}
              hideCode
              // $FlowFixMe - GroupsProfile uses graphql types, so we're missing __typename all over
              onClick={() => onGroupClicked(item)}
            />
          );
        })}
      </AvatarWithTextItems>
    );
  };

  handleAddGroup = () => {
    promiseRelationship(this.props.member.id)
      .then(relationship => {
        this.setState({
          addGroupModalRelationship: relationship,
          showAddGroupModal: true,
        });
      })
      .catch(errorLogger('PropleListUserPropfile handleAddGroup failed'));
  };

  handleSectionVisibilityChange = (uuidsOfExpandedSections: string[]) => {
    const { member, trackEvent } = this.props;

    if (uuidsOfExpandedSections.includes('Contact information')) {
      trackEvent('user_profile.devices.view', {
        affected_user_uuid: member.uuid,
      });
    }
  };

  handleNoteBlur = () => {
    const { onNoteChange } = this.props;
    const { note, noteChanged } = this.state;

    if (!noteChanged) {
      return;
    }

    onNoteChange(note);

    ToastActions.showSuccessToast('Note updated');
    this.setState({ noteChanged: false });
  };

  handleNoteChange = (event: SyntheticEvent<HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;

    this.setState(prevState => ({
      note: value,
      noteChanged: prevState.note !== value,
    }));
  };

  adminVisibleDevicesForUser = (user: UserProfile): Array<AdminVisibleDeviceDetails> => {
    if (user.adminVisibleDeviceDetails) {
      return filter(user.adminVisibleDeviceDetails, d => d.type === 'sms' || d.type === 'email');
    }
    return [];
  };

  getSections = () => {
    const { onFamilyMemberClicked, onUserChanged, member, trackEvent } = this.props;
    const {
      roles = [],
      familySummary,
      groupsJoined,
      groupsOwned,
      groupsInCommon,
      userInteractionPermissions,
    } = member;
    const { canAddChild, canAddParent, canCopyToAnotherGroup } = userInteractionPermissions;
    const { parents, children } = familySummary;
    const familyMemberCount = parents.length + children.length;

    const sections = [
      {
        title: 'Family members',
        children: (
          <GroupMemberFamily
            key={member.id}
            canAddChild={canAddChild}
            canAddParent={canAddParent}
            familySummary={familySummary}
            orientation="vertical"
            userId={member.id}
            onFamilyMemberClick={onFamilyMemberClicked}
            onAddFamilyMember={() => {
              trackEvent('user_profile.family_members.added');
              onUserChanged();
            }}
          />
        ),
        count: familyMemberCount,
        defaultOpen: !roles.includes('teacher') || !roles.includes('admin'),
      },
      {
        title: 'Classes owned',
        children: this.groupsList(groupsOwned, 'owned'),
        count: groupsOwned.length,
        defaultOpen: roles.includes('teacher'),
      },
    ];

    if (member.adminVisibleDeviceDetails) {
      const devices = this.adminVisibleDevicesForUser(member);

      sections.unshift({
        title: 'Contact information',
        children: this.adminDevicesList(devices),
        count: devices.length,
      });
    }

    if (groupsInCommon) {
      sections.push({
        title: 'Classes shared',
        children: this.groupsList(groupsInCommon, 'joined'),
        count: groupsInCommon.length,
      });
    }

    if (groupsJoined) {
      sections.push({
        title: 'Classes joined',
        children: (
          <React.Fragment>
            {this.groupsList(groupsJoined, 'joined')}
            {canCopyToAnotherGroup && (
              <AddButton listFooter size="large" onClick={this.handleAddGroup}>
                Add to another class
              </AddButton>
            )}
          </React.Fragment>
        ),
        count: groupsJoined.length,
      });
    }

    return sections;
  };

  renderActionButtons = () => {
    const { member, userActionsMenuButton } = this.props;
    const { userInteractionPermissions } = member;

    const { canChat, canPhone, canPhoneDenialReason } = userInteractionPermissions;

    const denialReason = canPhone
      ? undefined
      : ((canPhoneDenialReason || 'feature_not_available': any): DenialReason);

    return (
      <Buttons fullButtonIndex={0}>
        <StartChatButton
          disabled={!canChat}
          size="xsmall"
          subscriber={member.id}
          text="Send message"
        />
        <PhoneCallButton
          calleeName={member.name || ''}
          calleeUuid={member.uuid}
          glyphSize="inherit"
          size="xsmall"
          denialReason={denialReason}
        />
        {userActionsMenuButton}
      </Buttons>
    );
  };

  renderNoteField = () => {
    const { member } = this.props;
    const { note } = this.state;
    const { canSetNote } = member.userInteractionPermissions;

    return (
      <Textarea
        disabled={!canSetNote}
        placeholder="Add a private note"
        rows={2}
        value={note}
        onBlur={this.handleNoteBlur}
        onChange={this.handleNoteChange}
        data-test="note-field"
      />
    );
  };

  renderRosteredIcon() {
    const { member } = this.props;
    const { rosteredStatus } = member;

    // For the class level people list, we don't load or show rostering info
    if (rosteredStatus === null || rosteredStatus === undefined) {
      return null;
    }

    return (
      <TooltipTrigger
        placement="top"
        tooltip={
          <Tooltip>
            {rosteredStatus ? RosterConstants.rosteredLabel : RosterConstants.notRosteredLabel}
          </Tooltip>
        }>
        <span>
          <Icon
            color={
              rosteredStatus ? RosterConstants.rosteredColor : RosterConstants.notRosteredColor
            }
            name={rosteredStatus ? RosterConstants.rosteredIcon : RosterConstants.notRosteredIcon}
            size="xlarge"
          />
        </span>
      </TooltipTrigger>
    );
  }

  render() {
    const { addGroupModalRelationship, showAddGroupModal } = this.state;
    const { onBackClicked, onClose, onUserChanged, member } = this.props;

    return (
      <React.Fragment>
        <Profile
          actions={this.renderActionButtons()}
          errorBanner={null}
          loadingIndicator={null}
          noteField={this.renderNoteField()}
          sections={this.getSections()}
          onBackClicked={onBackClicked}
          onChange={this.handleSectionVisibilityChange}
          onClose={onClose}
          header={
            <UsersAvatarWithText
              avatarSize="xxlarge"
              icon={this.renderRosteredIcon()}
              level="primary"
              subtext={subtextForMember(member)}
              user={member}
            />
          }
          className="UsersProfile"
        />
        {addGroupModalRelationship && (
          <Modal isOpen={showAddGroupModal}>
            <CopySubscriberDialog
              relationships={[addGroupModalRelationship]}
              onRequestHide={this.closeModal}
              onSubscribe={onUserChanged}
            />
          </Modal>
        )}
      </React.Fragment>
    );
  }
}
